import React from 'react';
import PropTypes from 'prop-types';
import autobind from 'autobind-decorator';
import { action, computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router';
import loadable from '@loadable/component';

import { cn } from 'spa/css';

import { withConfig, withI18n } from 'spa/Components/Context';
import AddressField from 'spa/Components/AddressField';

import '../find-appointment-new.less';
import './find-appointment-new-for-digital.less';

import logoIcon from 'spa/images/icons/hygiene_roundel.svg';
import logoIconFR from 'spa/images/icons/hygiene_top_fr_white.svg';

const Tooltip = loadable(() => import('spa/Components/Tooltip'));

const MOBILE_BREAKPOINT_WIDTH = 600;

@inject(({
  store: {
    address,
    screen,
    tracker,
  },
  selection,
}) =>
  ({
    address,
    screen,
    tracker,
    selection,
  }))
@observer
@withI18n
@withConfig
class FindAppointmentNewForDigital extends React.Component {
  static propTypes = {
    address: PropTypes.shape({
      error: PropTypes.object,
      isLoading: PropTypes.bool.isRequired,
      load: PropTypes.func.isRequired,
      reset: PropTypes.func,
      response: PropTypes.object,
      search: PropTypes.object,
    }).isRequired,
    campaignCode: PropTypes.string,
    customButtonTextDesktop: PropTypes.string,
    customButtonTextMobile: PropTypes.string,
    customSpeciality: PropTypes.string,
    customVertical: PropTypes.string,
    isLandingPage: PropTypes.bool,
    phoneNumberFormatted: PropTypes.string,
    phoneNumberHuman: PropTypes.string,
    roundInputBox: PropTypes.bool,
    screen: PropTypes.object.isRequired,
    searchFieldOnly: PropTypes.bool,
    selection: PropTypes.shape({
      address_id: PropTypes.string,
      addressConfirmed: PropTypes.bool.isRequired,
      place_id: PropTypes.string,
    }).isRequired,
    subTitle: PropTypes.string,
    title: PropTypes.string,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
    trackingCategory: PropTypes.string,
    trackingLocation: PropTypes.string,
  };

  UNSAFE_componentWillMount() {
    const { config, i18n } = this.context;
    this.showAddressPicker = config.getValue('locale.b2c.address-picker-home-active', i18n.umRegion);

    /*
     *  If selection hasn't got place_id set, use last searched address for location
     */
    if (this.props.address && this.props.address.search && !this.props.selection.place_id) {
      this.props.address.search.term = '';
    }

    if (this.props.selection.place_id) {
      if (!this.props.address.search) {
        this.load(this.props.selection.place_id);
        this.isInitialLoad = true;
      }
    } else {
      this.props.selection.addressConfirmed = false;
    }

    this.showAvailableCities = config.getValue('b2c.locale.show-available-cities-info-homepage', i18n.umRegion);
  }

  @observable isInitialLoad = false;
  @observable showAvailableCities = false;
  @observable showToolTip = false;
  @observable showAddressPicker = false;

  @action('load - FindAppointmentNewForDigital')
  load(place_id) {
    const trackingData = {
      category: this.props.trackingCategory,
      label: place_id,
    };

    if (this.props.trackingLocation) {
      trackingData.data = {
        page: this.props.trackingLocation,
      };
    }

    this.props.selection.place_id = place_id;
    this.props.tracker.track('Load Location', trackingData);
    this.props.address.load(place_id)
      .then(() => {
        this.props.tracker.track('Load Location Success', trackingData);
        this.showToolTip = false;
      }, () => {
        this.props.tracker.track('Load Location Error', trackingData);
      });
    this.isInitialLoad = false;
  }

  @autobind
  @action('onClickSearch - FindAppointmentNewForDigital')
  onClickSearch(event) {
    this.showToolTip = false;
    /*
     *  If the search field is empty, don't allow search
     */
    if (!this.props.selection.place_id) {
      event.preventDefault();
      this.showToolTip = true;
    } else {
      const trackingData = {
        category: this.props.trackingCategory,
        label: this.props.selection.place_id,
      };

      if (this.props.trackingLocation) {
        trackingData.data = {
          page: this.props.trackingLocation,
        };
      }

      this.props.tracker.track('Clicked search in Find Appointment', trackingData);
    }
  }

  @autobind
  @action('onSelectAddress - FindAppointmentNewForDigital')
  onSelectAddress({ value }) {
    this.props.selection.addressConfirmed = false;
    this.load(value);
  }

  @autobind
  @action('onClickUseAddress - FindAppointmentNewForDigital')
  onClickUseAddress(event) {
    event.preventDefault();

    this.props.selection.addressConfirmed = true;

    const trackingData = {
      category: this.props.trackingCategory,
    };

    if (this.props.trackingLocation) {
      trackingData.data = {
        page: this.props.trackingLocation,
      };
    }

    this.props.tracker.track('Clicked Use Location', trackingData);
  }

  @autobind
  @action('onClickClearAddress - FindAppointmentNewForDigital')
  onClickClearAddress(event) {
    if (event) event.preventDefault();
    this.props.selection.addressConfirmed = false;
    this.props.selection.place_id = null;
    this.props.address.reset();
    const trackingData = {
      category: this.props.trackingCategory,
    };

    if (this.props.trackingLocation) {
      trackingData.data = {
        page: this.props.trackingLocation,
      };
    }

    this.props.tracker.track('Clicked Clear Location', trackingData);
  }

  @autobind
  @action('onCloseToolTip - FindAppointmentNewForDigital')
  onCloseToolTip(event) {
    event.preventDefault();
    this.showToolTip = false;
  }

  @autobind
  @action('onClickBrowseServices - FindAppointmentNewForDigital')
  onClickBrowseServices() {
    const trackingData = {
      category: this.props.trackingCategory,
      label: 'Browse Services',
    };

    if (this.props.trackingLocation) {
      trackingData.data = {
        page: this.props.trackingLocation,
      };
    }

    this.props.tracker.track('Clicked Browse Services button', trackingData);
  }

  @autobind addressFieldRef(addressField) { this.addressField = addressField; }

  @computed get isSearchActive() {
    return !!this.props.selection.place_id;
  }

  @computed get searchLink() {
    const { i18n } = this.context;
    const {
      campaignCode, customSpeciality, customVertical, isLandingPage, selection,
    } = this.props;
    const { place_id, address_id } = selection;
    const addressConfirmed = !isLandingPage && '&addressConfirmed';
    const selectedAddressId = address_id ? `&address_id=${address_id}` : '';
    let customSpecialityQuery = '';
    let customVerticalQuery = '';

    if (customSpeciality) {
      customSpecialityQuery = `&treatment=${customSpeciality}&treatmentConfirmed`;
    }

    if (customVertical) {
      customVerticalQuery = `&selectedVertical=${customVertical}`;
    }

    if (campaignCode) {
      return `/${i18n.lc}/booking/?place_id=${place_id}${selectedAddressId}&addressConfirmed&duration=1&people=1&therapists=1&promocode=${campaignCode}${customSpecialityQuery}${customVerticalQuery}`;
    }

    return `/${i18n.lc}/booking/?place_id=${place_id}${selectedAddressId}${addressConfirmed}&duration=1&people=1&therapists=1${customSpecialityQuery}${customVerticalQuery}`;
  }

  @computed get regionTitle() {
    const {i18n} = this.context;

    return i18n.umRegion === 'FR' ?
      i18n._x('Wellness for busy people', 'Homepage top section') :
      'Book at-home massage, beauty and more';
  }

  @computed get regionSubTitle() {
    const {subTitle} = this.props;
    const {i18n} = this.context;

    if (subTitle) {
      return <p className="find-appointment-new__paragraph" dangerouslySetInnerHTML={{ __html: subTitle }} />;
    }

    if (i18n.umRegion === 'FR') {
      return <p className="find-appointment-new__paragraph find-appointment-new-for-digital__paragraph">{i18n._x('Massage, osteopathy and more, at home and online.', 'Homepage top section')}</p>;
    }

    return <p className="find-appointment-new__paragraph find-appointment-new-for-digital__paragraph">Choose from over 50 treatments, then pick a qualified therapist – they can be with you in an hour.</p>;
  }

  render() {
    const { i18n } = this.context;
    const {searchLink, showAddressPicker, showAvailableCities } = this;
    const {
      customButtonTextDesktop,
      customButtonTextMobile,
      isLandingPage,
      phoneNumberHuman,
      phoneNumberFormatted,
      roundInputBox,
      screen,
      searchFieldOnly,
      selection,
      title,
      trackingCategory,
    } = this.props;
    const { address_id } = selection;
    const { isLoading, search } = this.props.address;
    const logo = i18n.region === 'FR' ? logoIconFR : logoIcon;

    if (searchFieldOnly) {
      return (
        showAddressPicker ? (
          <div className={cn(
            'row find-appointment-new__search-field-container',
            roundInputBox && 'find-appointment-new__search-field-container--round',
          )}
          >
            <div className="col-sm-8 find-appointment-new__search-field-input-wrapper">
              <AddressField
                className="find-appointment-new__search-field"
                clearStyles={{
                  display: 'block',
                  position: 'absolute',
                  top: '30%',
                  right: '1.5rem',
                  height: '1.8rem',
                  width: '1.8rem',
                }}
                defaultValue={search && search.term}
                disabled={isLoading}
                hideClearOnEmpty
                includeClear
                onSelect={this.onSelectAddress}
                onClear={this.onClickClearAddress}
                placeholder={i18n._x('Enter your address to find a professional', 'Home address picker')}
                ref={this.addressFieldRef}
                selectedAddressId={address_id}
                skipGeolocation
                skipInitialLoad={this.isInitialLoad}
                trackingCategory={trackingCategory}
              />
              {this.showToolTip &&
                <Tooltip
                  align={screen.width > MOBILE_BREAKPOINT_WIDTH ? 'center' : 'top'}
                  containerClassName="find-appointment-new__tooltip"
                  hide={!this.showToolTip}
                  onClose={this.onCloseToolTip}
                  theme="light"
                  width={screen.width > MOBILE_BREAKPOINT_WIDTH ? 400 : null}
                >
                  <span>
                    {i18n._x('Please enter your address', 'Find appointment box')}
                  </span>
                </Tooltip>
              }
            </div>
            <div className="col-sm-4">
              <Link
                className={cn(
                  'button button--primary button--caps find-appointment-new__search-button',
                  i18n.language === 'fr' && 'find-appointment-new__search-button--small',
                  roundInputBox && 'find-appointment-new__search-button--round',
                )}
                onClick={this.onClickSearch}
                to={searchLink}>
                <span className="find-appointment-new__search-button-text--mobile">{customButtonTextMobile || i18n._x('Search', 'homepage top section find appointments')}</span>
                <span className="find-appointment-new__search-button-text--desktop">{customButtonTextDesktop || i18n._x('Search', 'homepage top section find appointments')}</span>
              </Link>
            </div>
          </div>
        ) : (
          <div className="find-appointment-new__search-container">
            <Link
              className="button button--primary button--tiny-wide home-content-section__button home-content-section-for-digital__button find-appointment-new-for-digital__button "
              onClick={this.onClickBrowseServices}
              to={`/${i18n.lc}/treatments`}>
              {i18n._x('Find a wellness session', 'Homepage find a wellness session button')}
            </Link>
          </div>
        )
      );
    }

    return (
      <div className="find-appointment-new">
        <img alt="" className="find-appointment-new-for-digital__covid-image" src={logo} />
        {
          title ? (
            <h2 className="find-appointment-new__title" dangerouslySetInnerHTML={{ __html: title }} />
          ) : (
            <h2 className="find-appointment-new__title">
              {this.regionTitle}
            </h2>
          )
        }
        {this.regionSubTitle}

        {showAddressPicker ?
          <div className="find-appointment-new__search-container">
            <div className={cn(
              'row find-appointment-new__search-field-container',
              roundInputBox && 'find-appointment-new__search-field-container--round',
            )}>
              <div className="find-appointment-new__search-field-input-wrapper">
                <AddressField
                  data-qa="address-field-home"
                  className="find-appointment-new__search-field"
                  clearStyles={{
                    display: 'block',
                    position: 'absolute',
                    top: '30%',
                    right: '1.5rem',
                    height: '2.5rem',
                    width: '2.5rem',
                  }}
                  defaultValue={search && search.term}
                  disabled={isLoading}
                  hideClearOnEmpty
                  includeClear
                  onClear={this.onClickClearAddress}
                  onSelect={this.onSelectAddress}
                  placeholder={i18n._x('Enter your postcode', 'Home address picker')}
                  ref={this.addressFieldRef}
                  selectedAddressId={address_id}
                  skipGeolocation
                  skipInitialLoad={this.isInitialLoad}
                  trackingCategory={trackingCategory}
                />
                {this.showToolTip &&
                  <Tooltip
                    align={screen.width > MOBILE_BREAKPOINT_WIDTH ? 'center' : 'top'}
                    containerClassName="find-appointment-new__tooltip"
                    hide={!this.showToolTip}
                    onClose={this.onCloseToolTip}
                    theme="light"
                    width={screen.width > MOBILE_BREAKPOINT_WIDTH ? 400 : null}
                  >
                    <span>
                      {i18n._x('Please enter your address', 'Find appointment box')}
                    </span>
                  </Tooltip>
                }
              </div>
              {showAvailableCities ? (
                <div className="find-appointment-new__locations-container--mobile">
                  {i18n.region === 'FR' &&
                    <p className="find-appointment-new__locations-note">{i18n._x('We\'re in Paris', 'Homepage available cities string for France')}</p>
                  }
                  {i18n.region === 'GB' &&
                    <p className="find-appointment-new__locations-note">{i18n._x('We\'re in London, Manchester and Birmingham', 'Homepage available cities string for UK')}</p>
                  }
                  {phoneNumberHuman && phoneNumberFormatted &&
                    <a
                      href={`tel:${phoneNumberFormatted}`}
                      className="find-appointment-new__phone-number">
                      {i18n.__react('any questions? %(phoneNumber)s (8am - 10pm)', {
                        phoneNumber: phoneNumberHuman,
                      })}
                    </a>
                  }
                </div>
              ) : null}
              <div className="find-appointment-new__search-button-container">
                <Link
                  className={cn(
                    'button button--primary button--caps find-appointment-new__search-button',
                    i18n.language === 'fr' && 'find-appointment-new__search-button--small',
                    roundInputBox && 'find-appointment-new__search-button--round',
                  )}
                  data-qa="address-search-button-home"
                  onClick={this.onClickSearch}
                  to={searchLink}
                >
                  <span className="find-appointment-new__search-button-text--mobile">{customButtonTextMobile || i18n._x('Book now', 'homepage top section find appointments')}</span>
                  <span className="find-appointment-new__search-button-text--desktop">{customButtonTextDesktop || i18n._x('Book now', 'homepage top section find appointments')}</span>
                </Link>
              </div>
            </div>
            {showAvailableCities ? (
              <div className={cn(
                'find-appointment-new__locations-container',
                isLandingPage && 'find-appointment-new__locations-container--landing-page',
              )}>
                {i18n.region === 'FR' &&
                  <p className="find-appointment-new__locations-note">{i18n._x('We\'re in Paris', 'Homepage available cities string for France')}</p>
                }
                {i18n.region === 'GB' &&
                  <p className="find-appointment-new__locations-note">{i18n._x('We\'re in London, Manchester and Birmingham', 'Homepage available cities string for UK')}</p>
                }
                {phoneNumberHuman && phoneNumberFormatted &&
                  <a
                    href={`tel:${phoneNumberFormatted}`}
                    className="find-appointment-new__phone-number">
                    {i18n.__react('any questions? %(phoneNumber)s (8am - 10pm)', {
                      phoneNumber: phoneNumberHuman,
                    })}
                  </a>
                }
              </div>
            ) : null}
          </div>
          :
          <div className="find-appointment-new__search-container">
            <div className="find-appointmen-new-for-digital">
              <Link
                className="button button--primary button--tiny-wide home-content-section__button home-content-section-for-digital__button find-appointment-new-for-digital__button"
                onClick={this.onClickBrowseServices}
                to={`/${i18n.lc}/treatments`}>
                {i18n._x('Book at home or online', 'Homepage find a wellness session button')}
              </Link>
            </div>
          </div>
        }
      </div>
    );
  }
}

export default FindAppointmentNewForDigital;




// WEBPACK FOOTER //
// ./spa/Pages/Home2020/digital/FindAppointmentNewForDigital.jsx